import { MAX_FILES_UPLOAD_BYTES } from '@shared/constants/file-upload.constants';
import { ErrorMessage } from '@shared/constants/server-errors.constants';
import { isPdfFile } from '@shared/helpers/pdf-file.helpers';
import { ErrorCode, type FileRejection } from 'react-dropzone';

const UNSUPPORTED_FILE_MESSAGE = 'Only PDF files are supported';

export type EpdPdfDropResult =
  { type: 'accepted'; file: File } | { type: 'rejected'; message: string };

/** True when the file is past {@link MAX_FILES_UPLOAD_BYTES}. Equality is still allowed. */
const exceedsUploadLimit = (file: Pick<File, 'size'>): boolean =>
  file.size > MAX_FILES_UPLOAD_BYTES;

const isOversizedPdf = (rejection: FileRejection): boolean => {
  if (!isPdfFile(rejection.file)) return false;
  return rejection.errors.some(
    (error) => error.code === ErrorCode.FileTooLarge,
  );
};

/**
 * Chooses the PDF to upload, or a message to show when the drop is refused.
 * PDFs rejected only for a non-standard MIME type are still accepted.
 * An oversized PDF is refused with {@link ErrorMessage.FileTooLarge}, including
 * one that dropzone already placed in the accepted list.
 */
export const resolveEpdPdfDrop = (
  acceptedFiles: readonly File[],
  fileRejections: readonly FileRejection[],
): EpdPdfDropResult => {
  const accepted = acceptedFiles[0];
  // maxSize usually keeps these out of acceptedFiles; still enforce the shared limit.
  if (accepted && isPdfFile(accepted) && exceedsUploadLimit(accepted)) {
    return { type: 'rejected', message: ErrorMessage.FileTooLarge };
  }
  if (accepted) return { type: 'accepted', file: accepted };

  if (fileRejections.some(isOversizedPdf)) {
    return { type: 'rejected', message: ErrorMessage.FileTooLarge };
  }

  const pdfWithNonStandardType = fileRejections
    .map((rejection) => rejection.file)
    .find(isPdfFile);
  if (pdfWithNonStandardType && exceedsUploadLimit(pdfWithNonStandardType)) {
    return { type: 'rejected', message: ErrorMessage.FileTooLarge };
  }
  if (pdfWithNonStandardType) {
    return { type: 'accepted', file: pdfWithNonStandardType };
  }

  return { type: 'rejected', message: UNSUPPORTED_FILE_MESSAGE };
};
